import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl,
  RadioGroup, FormControlLabel, Radio
} from '@mui/material';

/**
 * Heliocentric zodiac criteria.
 *
 * Split out of the Zodiac tab, which carried geocentric and heliocentric sections under one
 * heading. They are different frames, not different sections: the Sun has no heliocentric position
 * and neither do the lunar nodes, so the two halves do not even offer the same bodies. Keeping them
 * on one tab meant a planet list that was right for one half and wrong for the other.
 */

/**
 * Bodies offered in this frame.
 *
 * Three exclusions, all for the same reason — the body has no position once the Sun is at the
 * centre, so a value here would be fiction rather than a poor approximation:
 *
 * - The Sun is the origin of the frame.
 * - Rahu and Ketu are the lunar nodes, the intersections of the Moon's orbit around EARTH with
 *   the ecliptic. Take Earth out of the centre and there is nothing left to intersect.
 *
 * The Moon stays. It orbits the Sun as well as the Earth, so it has a real heliocentric longitude
 * — one that tracks Earth's closely, which makes it the usable stand-in for Earth here.
 *
 * The backend refuses the nodes independently, so removing them from this list is about not
 * offering a choice that would be rejected, not about enforcement.
 */
const HELIO_PLANETS = ['Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto'];

const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

/** Where the zodiac starts, for the rows that measure in signs. */
const ZODIAC_FRAMES = [
  { value: 'tropical', label: 'Tropical' },
  { value: 'sidereal', label: 'Sidereal' },
];

/** What Row 5 counts the separation in. */
const SEPARATION_UNITS = [
  { value: 'signs', label: 'signs apart' },
  { value: 'nakshatras', label: 'nakshatras apart' },
];

export const ZODIAC_HELIO_INIT = {
  r1: { planet1: 'Moon', planet2: 'Mercury', diff: '', value: '2' },
  r2: { planet: 'Moon', longitude: '', value: '2' },
  r3: { planet: 'Moon', fromLon: '', toLon: '', value: '2' },
  r4: { planet1: 'Moon', planet2: 'Mercury', sign: 'Aries', frame: 'tropical', value: '2' },
  r5: { planet1: 'Moon', planet2: 'Mercury', count: '', unit: 'signs', value: '2' },
  r6: { planet: 'Moon', sign: 'Aries', frame: 'tropical', value: '2' },
};

const PLANET_SX = { minWidth: 110 };
const SIGN_SX = { minWidth: 130 };

const HelioPlanetSelect = ({ value, onChange, exclude }) => (
  <FormControl size="small" sx={PLANET_SX}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {HELIO_PLANETS.filter(p => !exclude || !exclude.includes(p)).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
    </Select>
  </FormControl>
);
HelioPlanetSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, exclude: PropTypes.array };

const SignSelect = ({ value, onChange }) => (
  <FormControl size="small" sx={SIGN_SX}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {SIGNS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
    </Select>
  </FormControl>
);
SignSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const Label = ({ children }) => (
  <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{children}</Typography>
);
Label.propTypes = { children: PropTypes.node.isRequired };

const NumField = ({ value, onChange, width }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)}
    sx={{ width: width || 80 }} />
);
NumField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, width: PropTypes.number };

const LonField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)}
    inputProps={{ min: 0, max: 359.99, step: 0.01 }} sx={{ width: 80 }} />
);
LonField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

/** An inline pair of radios. Row, not column, so it reads as part of the sentence. */
const InlineRadios = ({ value, onChange, options, name }) => (
  <RadioGroup row value={value} name={name} onChange={e => onChange(e.target.value)}
    sx={{ flexShrink: 0, ml: 0.5 }}>
    {options.map(o => (
      <FormControlLabel key={o.value} value={o.value}
        control={<Radio size="small" />}
        label={<Typography sx={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>{o.label}</Typography>}
        sx={{ mr: 1.5 }} />
    ))}
  </RadioGroup>
);
InlineRadios.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
};

const AddValueField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)}
    inputProps={{ min: 1, step: 1 }} sx={{ width: 56 }} />
);
AddValueField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddBtn = ({ onClick, disabled }) => (
  <Button variant="contained" size="small" onClick={onClick} disabled={disabled}
    sx={{ minWidth: 56, px: 1.5, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>
    ADD
  </Button>
);
AddBtn.propTypes = { onClick: PropTypes.func.isRequired, disabled: PropTypes.bool };

/**
 * The "Add value / n / ADD" tail, as one unit — same reasoning as the Esoterica++ tab.
 *
 * Loose children are wrap candidates in their own right, so a button at the end of a long row
 * breaks to a second line on its own and stops lining up with the rows above it. Grouped and
 * flexShrink: 0, the three move together and never give way; `ml: auto` pins them right.
 */
const AddCluster = ({ value, onChange, onAdd, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexShrink: 0 }}>
    <Label>Add value</Label>
    <AddValueField value={value} onChange={onChange} />
    <AddBtn onClick={onAdd} disabled={disabled} />
  </Box>
);
AddCluster.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const CriteriaRow = ({ children }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
    border: '1px solid #d0d0d0', borderRadius: 1, p: 1, mb: 1, backgroundColor: '#fafafa',
    minHeight: 56
  }}>
    {children}
  </Box>
);
CriteriaRow.propTypes = { children: PropTypes.node.isRequired };

const MOAZodiacHelioTab = ({ onAddCondition, forms: propForms, onFormsChange }) => {
  const [localForms, setLocalForms] = useState(ZODIAC_HELIO_INIT);
  const forms = propForms || localForms;
  const setForms = onFormsChange || setLocalForms;

  const set = (row, field, val) => setForms(f => ({ ...f, [row]: { ...f[row], [field]: val } }));

  /** Keeps a two-planet row from naming the same body twice. */
  const setPair = (row, field, v, other) => setForms(prev => {
    const updated = { ...prev[row], [field]: v };
    if (updated[other] === v) updated[other] = HELIO_PLANETS.find(p => p !== v) || HELIO_PLANETS[0];
    return { ...prev, [row]: updated };
  });

  const add = (type, condObj) => {
    const val = parseInt(condObj.value, 10);
    if (!val || val < 1) return;
    // group 2 is retained: these rows were group 2 before the split, and the group number is what
    // the backend keys the condition on. Renumbering them here would orphan every saved strategy.
    onAddCondition({ group: 2, type, ...condObj, value: val });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight="bold" fontSize={15} sx={{ textAlign: 'center', mb: 2, letterSpacing: 0.5 }}>
        Heliocentric Zodiac Criteria
      </Typography>

      {/* Row 1: Planet Planet Longitude difference */}
      <CriteriaRow>
        <Label>Planet</Label>
        <HelioPlanetSelect value={forms.r1.planet1} onChange={v => setPair('r1', 'planet1', v, 'planet2')} exclude={[forms.r1.planet2]} />
        <Label>Planet</Label>
        <HelioPlanetSelect value={forms.r1.planet2} onChange={v => setPair('r1', 'planet2', v, 'planet1')} exclude={[forms.r1.planet1]} />
        <Label>Longitude difference</Label>
        <NumField value={forms.r1.diff} onChange={v => set('r1', 'diff', v)} />
        <AddCluster value={forms.r1.value} onChange={v => set('r1', 'value', v)}
          disabled={!forms.r1.diff}
          onAdd={() => add('helio_longitude_difference', { planet1: forms.r1.planet1, planet2: forms.r1.planet2, diff: Number(forms.r1.diff), value: forms.r1.value })} />
      </CriteriaRow>

      {/* Row 2: Planet At Longitude, in tropical zodiac */}
      <CriteriaRow>
        <Label>Planet</Label>
        <HelioPlanetSelect value={forms.r2.planet} onChange={v => set('r2', 'planet', v)} />
        <Label>At Longitude</Label>
        <LonField value={forms.r2.longitude} onChange={v => set('r2', 'longitude', v)} />
        <Label>in tropical zodiac</Label>
        <AddCluster value={forms.r2.value} onChange={v => set('r2', 'value', v)}
          disabled={!forms.r2.longitude}
          onAdd={() => add('helio_at_longitude', { planet: forms.r2.planet, longitude: Number(forms.r2.longitude), value: forms.r2.value })} />
      </CriteriaRow>

      {/* Row 3: Planet from Longitude To Longitude, in tropical zodiac */}
      <CriteriaRow>
        <Label>Planet</Label>
        <HelioPlanetSelect value={forms.r3.planet} onChange={v => set('r3', 'planet', v)} />
        <Label>from Longitude</Label>
        <LonField value={forms.r3.fromLon} onChange={v => set('r3', 'fromLon', v)} />
        <Label>To Longitude</Label>
        <LonField value={forms.r3.toLon} onChange={v => set('r3', 'toLon', v)} />
        <Label>in tropical zodiac</Label>
        <AddCluster value={forms.r3.value} onChange={v => set('r3', 'value', v)}
          disabled={!forms.r3.fromLon || !forms.r3.toLon}
          onAdd={() => add('helio_longitude_range', { planet: forms.r3.planet, fromLon: Number(forms.r3.fromLon), toLon: Number(forms.r3.toLon), value: forms.r3.value })} />
      </CriteriaRow>

      {/* Row 4: Two planets together in a sign */}
      <CriteriaRow>
        <Label>Planet</Label>
        <HelioPlanetSelect value={forms.r4.planet1} onChange={v => setPair('r4', 'planet1', v, 'planet2')} exclude={[forms.r4.planet2]} />
        <Label>Planet</Label>
        <HelioPlanetSelect value={forms.r4.planet2} onChange={v => setPair('r4', 'planet2', v, 'planet1')} exclude={[forms.r4.planet1]} />
        <Label>in sign</Label>
        <SignSelect value={forms.r4.sign} onChange={v => set('r4', 'sign', v)} />
        <InlineRadios name="helio-r4-frame" value={forms.r4.frame} onChange={v => set('r4', 'frame', v)} options={ZODIAC_FRAMES} />
        <AddCluster value={forms.r4.value} onChange={v => set('r4', 'value', v)}
          onAdd={() => add('helio_two_planets_in_sign', { planet1: forms.r4.planet1, planet2: forms.r4.planet2, sign: forms.r4.sign, frame: forms.r4.frame, value: forms.r4.value })} />
      </CriteriaRow>

      {/* Row 5: Two planets a given number of signs or nakshatras apart */}
      <CriteriaRow>
        <Label>Planet</Label>
        <HelioPlanetSelect value={forms.r5.planet1} onChange={v => setPair('r5', 'planet1', v, 'planet2')} exclude={[forms.r5.planet2]} />
        <Label>Planet</Label>
        <HelioPlanetSelect value={forms.r5.planet2} onChange={v => setPair('r5', 'planet2', v, 'planet1')} exclude={[forms.r5.planet1]} />
        <Label>Are</Label>
        <NumField value={forms.r5.count} onChange={v => set('r5', 'count', v)} width={70} />
        <InlineRadios name="helio-r5-unit" value={forms.r5.unit} onChange={v => set('r5', 'unit', v)} options={SEPARATION_UNITS} />
        <AddCluster value={forms.r5.value} onChange={v => set('r5', 'value', v)}
          disabled={!forms.r5.count}
          onAdd={() => add('helio_planets_apart', { planet1: forms.r5.planet1, planet2: forms.r5.planet2, count: Number(forms.r5.count), unit: forms.r5.unit, value: forms.r5.value })} />
      </CriteriaRow>

      {/* Row 6: One planet in a sign */}
      <CriteriaRow>
        <Label>Planet</Label>
        <HelioPlanetSelect value={forms.r6.planet} onChange={v => set('r6', 'planet', v)} />
        <Label>in sign</Label>
        <SignSelect value={forms.r6.sign} onChange={v => set('r6', 'sign', v)} />
        <InlineRadios name="helio-r6-frame" value={forms.r6.frame} onChange={v => set('r6', 'frame', v)} options={ZODIAC_FRAMES} />
        <AddCluster value={forms.r6.value} onChange={v => set('r6', 'value', v)}
          onAdd={() => add('helio_planet_in_sign', { planet: forms.r6.planet, sign: forms.r6.sign, frame: forms.r6.frame, value: forms.r6.value })} />
      </CriteriaRow>

    </Box>
  );
};

MOAZodiacHelioTab.propTypes = {
  onAddCondition: PropTypes.func.isRequired,
  forms: PropTypes.object,
  onFormsChange: PropTypes.func,
};

export default MOAZodiacHelioTab;
